import { useState, useEffect, useRef } from 'react';
import { ArrowLeftRight, RefreshCw, Loader2 } from 'lucide-react';
import { usePagePermissions } from '../hooks/usePagePermissions';
import { usePaymentReconciliation } from '../hooks/usePaymentReconciliation';
import { paymentReconApi } from '../services/paymentReconApi';
import { useToast } from '../components/ui/Toast';
import { KpiTiles } from '../components/payment-recon/KpiTiles';
import { TransactionsTab } from '../components/payment-recon/TransactionsTab';
import { InvoicesTab } from '../components/payment-recon/InvoicesTab';
import { AgingTab, type AgingDrilldown } from '../components/payment-recon/AgingTab';
import { ImportTab } from '../components/payment-recon/ImportTab';
import { SettingsTab } from '../components/payment-recon/SettingsTab';
import { InvoiceCommentsPanel, type CommentTarget } from '../components/payment-recon/InvoiceCommentsPanel';

type Tab = 'reconcile' | 'invoices' | 'aging' | 'payments' | 'import' | 'settings';

const TABS: Array<{ key: Tab; label: string }> = [
    { key: 'invoices', label: 'Invoices' },
    { key: 'aging', label: 'Aging' },
    { key: 'payments', label: 'Payments' },
    { key: 'reconcile', label: 'Reconcile' },
    { key: 'import', label: 'Import' },
    { key: 'settings', label: 'Settings' },
];

export type InvoicesFilter = { customerCode: string | null; customerName: string | null; status: string; bucket: string | null };

const COMPANY_STORAGE_KEY = 'paymentRecon.company';

export function PaymentReconciliationPage() {
    const { canView, canEdit, isLoading: permLoading } = usePagePermissions('/payment-reconciliation');
    const { toast } = useToast();

    const [company, setCompany] = useState<string>(() => {
        try { return localStorage.getItem(COMPANY_STORAGE_KEY) || 'dfr'; } catch { return 'dfr'; }
    });
    const [companies, setCompanies] = useState<{ code: string; name: string }[]>([]);

    const {
        stats, nonCustomerCategories, residualReasons, bankAccounts, templates,
        loading, refreshStats, refreshReference, reloadAll,
    } = usePaymentReconciliation(company);

    const [tab, setTab] = useState<Tab>('invoices');
    const [invoicesFilter, setInvoicesFilter] = useState<InvoicesFilter | null>(null);
    const [syncing, setSyncing] = useState(false);
    const syncedCompanies = useRef<Set<string>>(new Set());

    // Per-invoice comments side-panel — opened from any tab/list on the page.
    const [commentsFor, setCommentsFor] = useState<CommentTarget | null>(null);
    // Bumped after a comment change so the open list re-fetches its count badges.
    const [commentsReloadKey, setCommentsReloadKey] = useState(0);

    // A payment to open straight away when landing on the Payments tab (set when the user
    // jumps from a bank-matched invoice to the payment that holds its allocation).
    const [openTransactionId, setOpenTransactionId] = useState<number | null>(null);

    // Manual tab navigation drops any drill-down filter carried into Invoices.
    const goTab = (next: Tab) => { setInvoicesFilter(null); setOpenTransactionId(null); setTab(next); };

    const gotoPayments = (transactionId?: number) => {
        setInvoicesFilter(null);
        setOpenTransactionId(transactionId ?? null);
        setTab('payments');
    };

    const drillToInvoices = (d: AgingDrilldown) => {
        // Always load the customer's open book; the bucket (if any) narrows client-side.
        setInvoicesFilter({ customerCode: d.customerCode, customerName: d.customerName, status: 'open', bucket: d.bucket });
        setTab('invoices');
    };

    // Load the companies the page can operate on (those with an invoice-source).
    useEffect(() => {
        void paymentReconApi.listCompanies()
            .then(res => {
                setCompanies(res.companies);
                setCompany(prev => (res.companies.some(c => c.code === prev) ? prev : (res.companies[0]?.code ?? prev)));
            })
            .catch(() => { /* selector falls back to the stored / default company */ });
    }, []);

    const changeCompany = (code: string) => {
        setCompany(code);
        try { localStorage.setItem(COMPANY_STORAGE_KEY, code); } catch { /* ignore */ }
    };

    // Auto-sync invoices once per company on first view (keeps the OP list current without a timer)
    useEffect(() => {
        if (permLoading || !canEdit || !company || syncedCompanies.current.has(company)) return;
        syncedCompanies.current.add(company);
        void (async () => {
            try {
                await paymentReconApi.syncInvoices(company);
                await refreshStats();
            } catch { /* non-fatal; manual sync button available */ }
        })();
    }, [permLoading, canEdit, company, refreshStats]);

    const manualSync = async () => {
        setSyncing(true);
        try {
            const res = await paymentReconApi.syncInvoices(company);
            toast(`Synced invoices: ${res.inserted} new, ${res.updated} updated`, 'success');
            await refreshStats();
        } catch (e) {
            toast(e instanceof Error ? e.message : 'Sync failed', 'error');
        } finally {
            setSyncing(false);
        }
    };

    if (permLoading) {
        return <div className="flex h-64 items-center justify-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>;
    }
    if (!canView) {
        return (
            <div className="mx-auto max-w-md py-20 text-center">
                <h2 className="text-lg font-semibold text-slate-700">No access</h2>
                <p className="mt-2 text-sm text-slate-500">You don't have permission to view Payment Reconciliation. Ask an admin to grant access in Settings.</p>
            </div>
        );
    }

    return (
        <div className="mx-auto max-w-7xl px-4 py-5">
            <div className="mb-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <ArrowLeftRight className="h-6 w-6 text-amber-600" />
                    <div>
                        <h1 className="text-xl font-semibold text-slate-800">Payment Reconciliation</h1>
                        <div className="mt-0.5 flex items-center gap-2 text-xs text-slate-500">
                            {companies.length > 0 ? (
                                <select
                                    value={company}
                                    onChange={(e) => changeCompany(e.target.value)}
                                    className="rounded border border-slate-300 bg-white px-2 py-1 text-xs font-medium text-slate-700 focus:border-amber-400 focus:outline-none"
                                >
                                    {companies.map(c => <option key={c.code} value={c.code}>{c.name} ({c.code})</option>)}
                                </select>
                            ) : (
                                <span className="font-medium text-slate-700">{company}</span>
                            )}
                            <span>· match bank credits to open invoices</span>
                        </div>
                    </div>
                </div>
                {canEdit && (
                    <button
                        onClick={manualSync}
                        disabled={syncing}
                        className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                    >
                        {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Sync invoices
                    </button>
                )}
            </div>

            <div className="mb-4">
                <KpiTiles stats={stats} onUnresolvedClick={() => goTab('reconcile')} />
            </div>

            {/* Tabs */}
            <div className="mb-4 flex gap-1 border-b border-slate-200">
                {TABS.map(t => (
                    <button
                        key={t.key}
                        onClick={() => goTab(t.key)}
                        className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium ${tab === t.key ? 'border-amber-500 text-amber-700' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
                    >
                        {t.label}
                    </button>
                ))}
            </div>

            {loading && !stats ? (
                <div className="py-10 text-center text-sm text-slate-400">Loading…</div>
            ) : (
                <>
                    {tab === 'reconcile' && (
                        <TransactionsTab
                            canEdit={canEdit}
                            companyCode={company}
                            nonCustomerCategories={nonCustomerCategories}
                            residualReasons={residualReasons}
                            defaultStatus="unresolved"
                            onChanged={refreshStats}
                            onOpenComments={setCommentsFor}
                            commentsReloadKey={commentsReloadKey}
                        />
                    )}
                    {tab === 'payments' && (
                        <TransactionsTab
                            canEdit={canEdit}
                            companyCode={company}
                            nonCustomerCategories={nonCustomerCategories}
                            residualReasons={residualReasons}
                            defaultStatus="all"
                            onChanged={refreshStats}
                            onOpenComments={setCommentsFor}
                            commentsReloadKey={commentsReloadKey}
                            openTransactionId={openTransactionId}
                            onOpenedTransaction={() => setOpenTransactionId(null)}
                        />
                    )}
                    {tab === 'invoices' && <InvoicesTab canEdit={canEdit} companyCode={company} initialFilter={invoicesFilter} onChanged={refreshStats} onGotoPayments={gotoPayments} onOpenComments={setCommentsFor} commentsReloadKey={commentsReloadKey} />}
                    {tab === 'aging' && <AgingTab canEdit={canEdit} companyCode={company} onDrilldown={drillToInvoices} />}
                    {tab === 'import' && (
                        <ImportTab
                            canEdit={canEdit}
                            companyCode={company}
                            bankAccounts={bankAccounts}
                            templates={templates}
                            onTemplatesChanged={refreshReference}
                            onImported={() => { void reloadAll(); }}
                        />
                    )}
                    {tab === 'settings' && (
                        <SettingsTab canEdit={canEdit} companyCode={company} bankAccounts={bankAccounts} onBankAccountsChanged={refreshReference} />
                    )}
                </>
            )}

            {commentsFor && (
                <InvoiceCommentsPanel
                    target={commentsFor}
                    canEdit={canEdit}
                    onClose={() => setCommentsFor(null)}
                    onChanged={() => setCommentsReloadKey(k => k + 1)}
                />
            )}
        </div>
    );
}
